import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Login from './pages/Login';                              // eager (auth screen)
import Layout from './components/Layout';                       // eager (shell)
import { Toaster } from './components/ui/sonner';
import PwaInstallPrompt from './components/PwaInstallPrompt';

// ---- Code-split every page into its own webpack chunk.
// Cuts the initial JS bundle from ~2 MB gzipped to ~400 KB — the browser now
// only downloads the page(s) the user actually opens.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const Deals = lazy(() => import('./pages/Deals'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Analytics = lazy(() => import('./pages/Analytics'));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'));
const Reminders = lazy(() => import('./pages/Reminders'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Stores = lazy(() => import('./pages/Stores'));
const Departments = lazy(() => import('./pages/Departments'));
const Warehouse = lazy(() => import('./pages/Warehouse'));
const Accounts = lazy(() => import('./pages/Accounts'));
const CompanyModule = lazy(() => import('./pages/CompanyModule'));
const EDP = lazy(() => import('./pages/EDP'));
const Packing = lazy(() => import('./pages/Packing'));
const Logistics = lazy(() => import('./pages/Logistics'));
const RawMaterials = lazy(() => import('./pages/RawMaterials'));
const Ecommerce = lazy(() => import('./pages/Ecommerce'));
const Reports = lazy(() => import('./pages/Reports'));
const StoreReports = lazy(() => import('./pages/StoreReports'));
const BranchActivities = lazy(() => import('./pages/BranchActivities'));
const StockCheck = lazy(() => import('./pages/StockCheck'));
const Chat = lazy(() => import('./pages/Chat'));
const StaffAttendance = lazy(() => import('./pages/StaffAttendance'));
const UserActivity = lazy(() => import('./pages/UserActivity'));
const EmailSchedules = lazy(() => import('./pages/EmailSchedules'));
const StoreOpening = lazy(() => import('./pages/StoreOpening'));
const PurchaseRateFixing = lazy(() => import('./pages/PurchaseRateFixing'));
const CustomerFrequency = lazy(() => import('./pages/CustomerFrequency'));
const AddressPrint = lazy(() => import('./pages/AddressPrint'));
const StoreRequests = lazy(() => import('./pages/StoreRequests'));
const WorkflowTasks = lazy(() => import('./pages/WorkflowTasks'));
const WorkflowApprovals = lazy(() => import('./pages/WorkflowApprovals'));
const WorkflowMeetings = lazy(() => import('./pages/WorkflowMeetings'));
const BarcodeVerify = lazy(() => import('./pages/BarcodeVerify'));
const SalesGstReport = lazy(() => import('./pages/SalesGstReport'));
const B2CSales = lazy(() => import('./pages/B2CSales'));
const SalesPurchaseCompare = lazy(() => import('./pages/SalesPurchaseCompare'));
const DistributionHub = lazy(() => import('./pages/DistributionHub'));
const AiHelper = lazy(() => import('./pages/AiHelper'));
const BranchCash = lazy(() => import('./pages/BranchCash'));
const VoiceNotes = lazy(() => import('./pages/VoiceNotes'));
const BillMarginAnalyzer = lazy(() => import('./pages/BillMarginAnalyzer'));

// Tiny inline fallback so route transitions don't flash a blank white screen.
const PageFallback = () => (
  <div className="flex items-center justify-center h-screen text-[#94A3B8] text-sm">
    <div className="animate-pulse">Loading…</div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [allowedStores, setAllowedStores] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('crm_token');
    const userData = localStorage.getItem('crm_user');
    const permsData = localStorage.getItem('crm_permissions');
    const storesData = localStorage.getItem('crm_allowed_stores');

    if (token && userData) {
      setUser(JSON.parse(userData));
      if (permsData) setPermissions(JSON.parse(permsData));
      if (storesData) setAllowedStores(JSON.parse(storesData));

      // Silently refresh permissions from server so newly-granted access shows up
      // without forcing the user to log out and back in.
      (async () => {
        try {
          const axios = (await import('axios')).default;
          const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.data?.permissions) {
            localStorage.setItem('crm_permissions', JSON.stringify(r.data.permissions));
            setPermissions(r.data.permissions);
          }
          if (r.data?.allowed_stores) {
            localStorage.setItem('crm_allowed_stores', JSON.stringify(r.data.allowed_stores));
            setAllowedStores(r.data.allowed_stores);
          }
        } catch (_) { /* stale token / offline — keep cached copy */ }
      })();
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData, perms, stores) => {
    localStorage.setItem('crm_token', token);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    if (perms) localStorage.setItem('crm_permissions', JSON.stringify(perms));
    if (stores) localStorage.setItem('crm_allowed_stores', JSON.stringify(stores));
    setUser(userData);
    setPermissions(perms || null);
    setAllowedStores(stores || null);
  };

  const handleLogout = () => {
    const adminToken = localStorage.getItem('crm_admin_token');
    const adminUser = localStorage.getItem('crm_admin_user');
    const adminPerms = localStorage.getItem('crm_admin_permissions');
    const adminStores = localStorage.getItem('crm_admin_allowed_stores');
    
    if (adminToken && adminUser) {
      localStorage.setItem('crm_token', adminToken);
      localStorage.setItem('crm_user', adminUser);
      if (adminPerms) localStorage.setItem('crm_permissions', adminPerms);
      if (adminStores) localStorage.setItem('crm_allowed_stores', adminStores);
      localStorage.removeItem('crm_admin_token');
      localStorage.removeItem('crm_admin_user');
      localStorage.removeItem('crm_admin_permissions');
      localStorage.removeItem('crm_admin_allowed_stores');
      setUser(JSON.parse(adminUser));
      setPermissions(adminPerms ? JSON.parse(adminPerms) : null);
      setAllowedStores(adminStores ? JSON.parse(adminStores) : null);
      return;
    }
    
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    localStorage.removeItem('crm_permissions');
    localStorage.removeItem('crm_allowed_stores');
    setUser(null);
    setPermissions(null);
    setAllowedStores(null);
  };

  const handleImpersonate = (token, userData, perms, stores) => {
    localStorage.setItem('crm_admin_token', localStorage.getItem('crm_token'));
    localStorage.setItem('crm_admin_user', localStorage.getItem('crm_user'));
    localStorage.setItem('crm_admin_permissions', localStorage.getItem('crm_permissions') || '');
    localStorage.setItem('crm_admin_allowed_stores', localStorage.getItem('crm_allowed_stores') || '[]');
    localStorage.setItem('crm_token', token);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    if (perms) localStorage.setItem('crm_permissions', JSON.stringify(perms));
    if (stores) localStorage.setItem('crm_allowed_stores', JSON.stringify(stores));
    setUser(userData);
    setPermissions(perms || null);
    setAllowedStores(stores || null);
  };

  const isImpersonating = !!localStorage.getItem('crm_admin_token');

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route 
            path="/login" 
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/" 
            element={user ? <Layout user={user} permissions={permissions} onLogout={handleLogout} isImpersonating={isImpersonating} /> : <Navigate to="/login" />}
          >
            <Route index element={<Dashboard user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="dashboard" element={<Navigate to="/" replace />} />
            <Route path="contacts" element={<Contacts user={user} />} />
            <Route path="contacts/:id" element={<ContactDetail user={user} />} />
            <Route path="deals" element={<Deals user={user} />} />
            <Route path="tasks" element={<Tasks user={user} />} />
            <Route path="todo" element={<Tasks user={user} permissions={permissions} />} />
            <Route path="reminders" element={<Reminders user={user} />} />
            <Route path="email-templates" element={<EmailTemplates user={user} />} />
            <Route path="stores" element={<Stores user={user} />} />
            <Route path="departments" element={<Departments user={user} />} />
            <Route path="dept/branch_activities" element={<BranchActivities user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="stock-check" element={<StockCheck user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="dept/warehouse" element={<Warehouse user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="dept/accounts" element={<Accounts user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="company-module" element={<CompanyModule user={user} permissions={permissions} />} />
            <Route path="user-activity" element={<UserActivity user={user} />} />
            <Route path="email-schedules" element={<EmailSchedules user={user} />} />
            <Route path="dept/edp" element={<EDP user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="dept/packing" element={<Packing user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="dept/logistics" element={<Logistics user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="dept/raw_materials" element={<RawMaterials user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="dept/ecommerce" element={<Ecommerce user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="reports" element={<Reports user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="chat" element={<Chat user={user} permissions={permissions} />} />
            <Route path="staff-attendance" element={<StaffAttendance user={user} permissions={permissions} allowedStores={allowedStores} />} />
            <Route path="store-opening" element={<StoreOpening user={user} permissions={permissions} />} />
            <Route path="purchase-rate" element={<PurchaseRateFixing user={user} permissions={permissions} />} />
            <Route path="customer-frequency" element={<CustomerFrequency user={user} permissions={permissions} />} />
            <Route path="address-print" element={<AddressPrint user={user} permissions={permissions} />} />
            <Route path="store-requests" element={<StoreRequests user={user} permissions={permissions} />} />
            <Route path="workflow-tasks" element={<WorkflowTasks user={user} />} />
            <Route path="workflow-approvals" element={<WorkflowApprovals user={user} />} />
            <Route path="workflow-meetings" element={<WorkflowMeetings user={user} />} />
            <Route path="barcode-verify" element={<BarcodeVerify user={user} />} />
            <Route path="sales-gst-report" element={<SalesGstReport user={user} />} />
            <Route path="b2c-sales" element={<B2CSales user={user} />} />
            <Route path="sales-purchase-compare" element={<SalesPurchaseCompare user={user} />} />
            <Route path="distribution-hub" element={<DistributionHub user={user} />} />
            <Route path="ai-helper" element={<AiHelper user={user} />} />
            <Route path="branch-cash" element={<BranchCash user={user} />} />
            <Route path="voice-notes" element={<VoiceNotes user={user} />} />
            <Route path="bill-analyzer" element={<BillMarginAnalyzer user={user} permissions={permissions} />} />
            <Route path="dept/:department" element={<Departments user={user} />} />
            <Route path="store-reports" element={<StoreReports user={user} />} />
            <Route path="analytics" element={<Analytics user={user} />} />
            <Route path="users" element={<UserManagement user={user} onImpersonate={handleImpersonate} />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
      <PwaInstallPrompt />
    </div>
  );
}

export default App;
